/**
 * Companies Service
 * Integrates with the new Spring Boot backend for company registration
 */

import environmentConfig from '../config/environment';
import logger from '../utils/logger';
import { buildJsonAuthHeaders } from '../utils/authHeaders';
import { appendAcquisitionMetadata } from './acquisitionMetadata';

class CompaniesService {
  constructor() {
    this.apiBase = environmentConfig.api.baseURL;
    this.endpoints = {
      register: '/api/companies/register',
      initial: '/api/companies/initial',
      details: '/api/companies/details',
      byUser: '/api/companies/by-user',
      status: '/api/companies/status',
    };
  }

  /**
   * Get auth headers with token
   */
  getAuthHeaders(token) {
    return buildJsonAuthHeaders(token);
  }

  /**
   * Create initial registration (simplified flow)
   * @param {Object} data - Registration data
   * @param {string} data.companyName - Company name
   * @param {string} data.planId - Selected plan UUID
   * @param {Array<string>} data.addOnIds - Selected add-on UUIDs
   * @param {number} data.quantity - Quantity (default 1)
   * @param {string} token - Auth token
   * @returns {Promise<Object>} Registration response with Stripe client secret
   */
  async createInitialRegistration(data, token) {
    try {
      logger.info('Creating initial registration', {
        companyName: data.companyName,
        planId: data.planId,
      });

      const payload = appendAcquisitionMetadata({
        companyName: data.companyName,
        planId: data.planId,
        addOnIds: data.addOnIds || [],
        quantity: data.quantity || 1,
      });

      const response = await fetch(`${this.apiBase}${this.endpoints.register}`, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Registration failed: ${response.statusText}`
        );
      }

      const result = await response.json();
      logger.info('Registration created successfully', {
        registrationId: result.registrationId,
      });

      return {
        registrationId: result.registrationId,
        companyName: result.companyName,
        planId: result.planId,
        addOnIds: result.addOnIds,
        totalAmount: parseFloat(result.totalAmount),
        currency: result.currency,
        stripeClientSecret: result.stripeClientSecret,
        stripePaymentIntentId: result.stripePaymentIntentId,
      };
    } catch (error) {
      logger.error('Failed to create initial registration', error);
      throw error;
    }
  }

  /**
   * Submit company details after payment
   * @param {string} registrationId - Registration UUID
   * @param {Object} details - Company details
   * @param {string} token - Auth token
   * @returns {Promise<Object>} Updated registration
   */
  async submitCompanyDetails(registrationId, details, token) {
    try {
      logger.info('Submitting company details', { registrationId });

      const response = await fetch(`${this.apiBase}${this.endpoints.details}`, {
        method: 'POST',
        headers: this.getAuthHeaders(token),
        body: JSON.stringify({
          registrationId,
          ...details,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to submit details: ${response.statusText}`
        );
      }

      const result = await response.json();
      logger.info('Company details submitted successfully');

      return result;
    } catch (error) {
      logger.error('Failed to submit company details', error);
      throw error;
    }
  }

  /**
   * Get user's companies/registrations
   * @param {string} token - Auth token
   * @returns {Promise<Array>} List of companies
   */
  async getUserCompanies(token) {
    try {
      logger.info('Fetching user companies');

      const response = await fetch(`${this.apiBase}${this.endpoints.byUser}`, {
        method: 'GET',
        headers: this.getAuthHeaders(token),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.statusText}`);
      }

      const data = await response.json();
      logger.info('User companies fetched', {
        count: data.data?.length || 0,
      });

      // Backend returns { success, message, data: [...] }
      return data.data || [];
    } catch (error) {
      logger.error('Failed to fetch user companies', error);
      throw error;
    }
  }

  /**
   * Get registration status
   * @param {string} registrationId - Registration UUID
   * @param {string} token - Auth token
   * @returns {Promise<Object>} Registration status
   */
  async getRegistrationStatus(registrationId, token) {
    try {
      logger.info('Fetching registration status', { registrationId });

      const response = await fetch(
        `${this.apiBase}${this.endpoints.status}/${registrationId}`,
        {
          method: 'GET',
          headers: this.getAuthHeaders(token),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch status: ${response.statusText}`);
      }

      const data = await response.json();
      logger.info('Registration status fetched', { stage: data.currentStage });

      return data;
    } catch (error) {
      logger.error('Failed to fetch registration status', error);
      throw error;
    }
  }
}

// Export singleton instance
const companiesService = new CompaniesService();
export default companiesService;
